<template>
  <div>
    <v-dialog v-model="internalValue" max-width="900px" persistent scrollable>
      <v-card class="ds-dialog">
        <!-- Header -->
        <v-card-title class="ds-dialog-header py-4">
          <v-icon color="white" class="mr-3" style="position: relative; z-index: 1;">mdi-file-document-edit</v-icon>
          <span class="ds-dialog-title">Prescrever</span>
          <v-spacer></v-spacer>
          <v-btn icon color="white" aria-label="Ver tour" @click="startTour">
            <v-icon>mdi-help-circle-outline</v-icon>
          </v-btn>
          <v-btn
            icon
            class="close-btn"
            @click="closeDialog"
            color="white"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <section class="ds-form-section" data-tour="pd2-tipo">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-format-list-checks</v-icon>
                <span>Tipo</span>
              </div>
            </div>
            <v-radio-group
              v-model="prescriptionType"
              row
              hide-details
              class="mt-0 pt-0"
              @change="handlePrescriptionTypeChange"
            >
              <v-radio label="Medicamentos" value="medication" color="primary">
                <template v-slot:label>
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-2">mdi-pill</v-icon>
                    <span class="font-weight-bold">Medicamentos</span>
                  </div>
                </template>
              </v-radio>

              <v-radio label="Exames" value="exam" color="success">
                <template v-slot:label>
                  <div class="d-flex align-center">
                    <v-icon color="success" class="mr-2">mdi-test-tube</v-icon>
                    <span class="font-weight-bold">Exames</span>
                  </div>
                </template>
              </v-radio>

              <v-radio label="Suplementos" value="supplement" color="warning">
                <template v-slot:label>
                  <div class="d-flex align-center">
                    <v-icon color="warning" class="mr-2">mdi-leaf</v-icon>
                    <span class="font-weight-bold">Suplementos</span>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
          </section>

          <!-- Toolbar -->
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-format-text</v-icon>
                <span>Ferramentas de Formatação</span>
              </div>
            </div>

            <div class="ds-toolbar">
              <v-btn-toggle dense class="mr-2">
                <v-btn small @click="format('bold')" title="Negrito">
                  <v-icon size="18">mdi-format-bold</v-icon>
                </v-btn>
                <v-btn small @click="format('italic')" title="Itálico">
                  <v-icon size="18">mdi-format-italic</v-icon>
                </v-btn>
                <v-btn
                  small
                  @click="format('underline')"
                  title="Sublinhado"
                >
                  <v-icon size="18">mdi-format-underline</v-icon>
                </v-btn>
              </v-btn-toggle>

              <v-btn-toggle dense class="mr-2">
                <v-btn
                  small
                  @click="format('insertOrderedList')"
                  title="Lista numerada"
                >
                  <v-icon size="18">mdi-format-list-numbered</v-icon>
                </v-btn>
                <v-btn
                  small
                  @click="format('insertUnorderedList')"
                  title="Lista com marcadores"
                >
                  <v-icon size="18">mdi-format-list-bulleted</v-icon>
                </v-btn>
              </v-btn-toggle>

              <v-btn-toggle dense class="mr-2">
                <v-btn
                  small
                  @click="clearFormatting"
                  title="Limpar formatação"
                >
                  <v-icon size="18">mdi-format-clear</v-icon>
                </v-btn>
                <v-btn small @click="format('undo')" title="Desfazer">
                  <v-icon size="18">mdi-undo</v-icon>
                </v-btn>
                <v-btn small @click="format('redo')" title="Refazer">
                  <v-icon size="18">mdi-redo</v-icon>
                </v-btn>
              </v-btn-toggle>

              <!-- Ditado por voz -->
              <DictationButton
                data-tour="pd2-ditado"
                ref="dictation"
                :color="typeColor(prescriptionType)"
                @transcript="onDictationTranscript"
                @recording="onDictationRecordingChange"
              />
            </div>

            <div class="mt-2 d-flex justify-space-between align-center">
              <v-chip
                small
                outlined
                :color="typeColor(prescriptionType)"
              >
                Use <strong>@</strong> para buscar
                {{ typeLabelPlural(prescriptionType) }}
              </v-chip>

              <div>
                <v-btn
                  small
                  outlined
                  color="error"
                  @click="clearAll"
                  class="mr-2"
                  :disabled="characterCount === 0 && selectedItems.length === 0"
                >
                  <v-icon size="18" class="mr-1">mdi-delete-outline</v-icon>
                  Limpar Tudo
                </v-btn>

                <v-btn
                  small
                  outlined
                  color="primary"
                  @click="openReview"
                  :disabled="characterCount === 0"
                  class="mr-2"
                >
                  <v-icon size="18" class="mr-1">mdi-file-eye-outline</v-icon>
                  Revisar
                </v-btn>

                <v-btn
                  small
                  outlined
                  color="primary"
                  @click="printPrescription"
                  :disabled="characterCount === 0"
                >
                  <v-icon size="18" class="mr-1">mdi-printer</v-icon>
                  Imprimir
                </v-btn>
              </div>
            </div>
          </section>

          <!-- Editor -->
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-text-box-outline</v-icon>
                <span>Conteúdo da Prescrição</span>
              </div>
            </div>

            <div
              class="editor-wrapper"
              data-tour="pd2-busca"
              :class="{ 'editor-focused': isFocused, 'editor-error': hasError }"
            >
              <div
                ref="editor"
                contenteditable="true"
                class="rich-editor"
                @input="updateContent"
                @keydown="handleKeydown"
                @keyup="checkAutocomplete"
                @focus="isFocused = true"
                @blur="handleBlur"
                @paste="handlePaste"
                :placeholder="`Digite a prescrição... Use @ para buscar ${typeLabelPlural(
                  prescriptionType,
                )}`"
              ></div>

              <div v-if="apiLoading" class="editor-loading">
                <v-progress-circular
                  indeterminate
                  size="20"
                  width="2"
                  color="primary"
                ></v-progress-circular>
                <span class="text-caption ml-2">Buscando medicamentos...</span>
              </div>
            </div>

            <v-alert
              v-if="hasError"
              type="error"
              dense
              class="mt-3"
              dismissible
              @input="hasError = false"
            >
              Por favor, preencha a prescrição antes de salvar
            </v-alert>
          </section>

          <!-- Menu de Autocomplete -->
          <v-menu
            v-model="showAutocomplete"
            :close-on-content-click="false"
            :position-x="menuPosition.x"
            :position-y="menuPosition.y"
            absolute
            offset-y
          >
            <v-card class="autocomplete-card">
              <v-card-title class="py-2 px-3">
                <v-icon
                  size="18"
                  :color="typeColor(prescriptionType)"
                  class="mr-2"
                  >mdi-magnify</v-icon
                >
                <span class="text-subtitle-2">{{
                  typeLabelPluralCapitalized(prescriptionType)
                }}</span>
                <v-spacer></v-spacer>
                <v-chip x-small
                  >{{ filteredItems.length }} resultados</v-chip
                >
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="pa-0">
                <v-list
                  v-if="filteredItems.length > 0"
                  class="autocomplete-list"
                >
                  <v-list-item
                    v-for="(item, index) in filteredItems"
                    :key="index"
                    @click="handleItemSelection(item)"
                    :class="{ 'selected-item': index === selectedIndex }"
                    class="autocomplete-item"
                  >
                    <template v-slot:prepend>
                      <v-icon :color="typeColor(prescriptionType)">
                        {{ typeIcon(prescriptionType) }}
                      </v-icon>
                    </template>
                    <v-list-item-title>
                      {{ item.name }}
                      <v-chip
                        v-if="item.fromPatient"
                        x-small
                        class="ml-2"
                        :color="item.patientActive ? '#F0FDF4' : '#F5F5F4'"
                        :text-color="item.patientActive ? '#15803D' : '#78716C'"
                      >
                        {{ item.patientBadge }}
                      </v-chip>
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="item.description">{{
                      item.description
                    }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
                <div v-else class="pa-4 text-center text-caption">
                  Nenhum
                  {{ typeLabelSingular(prescriptionType) }}
                  encontrado
                </div>
              </v-card-text>
            </v-card>
          </v-menu>
        </v-card-text>

        <!-- Alerta de interacao medicamentosa em tempo real (T1.6c) -->
        <v-alert
          v-if="showInteractionAlert"
          :type="interactionAlertType"
          text
          dense
          class="mx-5 my-3"
          :icon="
            interactionPairs.length > 0
              ? 'mdi-pill-off'
              : 'mdi-help-circle-outline'
          "
        >
          <div v-if="interactionPairs.length > 0" class="text-subtitle-2 mb-1">
            {{ interactionPairs.length }} possível(is) interação(ões)
            medicamentosa(s) detectada(s)
          </div>
          <ul
            v-if="interactionPairs.length > 0"
            class="pl-4 mb-0"
            style="font-size: 0.85rem"
          >
            <li v-for="(p, i) in interactionPairs" :key="i">
              <strong>{{ p.a }} × {{ p.b }}</strong>
              <span class="text-uppercase"> ({{ p.severity }})</span>
              <template v-if="p.note"> — {{ p.note }}</template>
              <!-- A conduta e o que transforma o alerta em decisao clinica. -->
              <div v-if="p.management" class="mt-1 font-italic">
                Conduta: {{ p.management }}
              </div>
            </li>
          </ul>

          <!--
            SEGURANCA CLINICA (SPEC T1.6 §5.1): sem este aviso, um farmaco que a
            base nao conhece produz a MESMA tela de "nenhuma interacao", que o
            medico le como via livre. Dizer o que nao foi verificado e o unico
            jeito de nao passar falsa seguranca.
          -->
          <div
            v-if="interactionUnrecognized.length > 0"
            :class="
              interactionPairs.length > 0 ? 'mt-2 pt-2 interaction-divider' : ''
            "
            style="font-size: 0.85rem"
          >
            <strong>Não foi possível verificar</strong>
            {{ interactionUnrecognized.join(", ") }} — não consta na nossa base.
            Ausência de alerta aqui não significa ausência de interação.
          </div>
        </v-alert>

        <!-- Footer -->
        <v-card-actions class="ds-dialog-actions">
          <v-chip small class="footer-chip">
            <v-icon x-small left>mdi-text</v-icon>
            {{ characterCount }} caracteres
          </v-chip>
          <v-chip
            small
            :color="typeColor(prescriptionType)"
            class="footer-chip"
            data-tour="pd2-itens"
          >
            <v-icon x-small left>{{ typeIcon(prescriptionType) }}</v-icon>
            {{ selectedItems.length }}
            {{ typeLabelPlural(prescriptionType) }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn outlined color="primary" class="ds-cancel-btn" @click="closeDialog">
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-btn color="primary" depressed class="ds-save-btn" data-tour="pd2-salvar" @click="savePrescription" :loading="isSaving"
            >Salvar</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Detalhes do Medicamento -->
    <MedicationDetailsDialog
      v-model="showMedicationDetails"
      :medication="currentMedication"
      :patient-id="patientId"
      @cancel="cancelMedicationDetails"
      @confirm="confirmMedicationDetails"
    />

    <!-- Confirmação de CID para exame/suplemento (busca) -->
    <ItemCidConfirmDialog
      v-model="showItemCidDialog"
      :item="pendingDirectItem"
      :patient-id="patientId"
      :prescription-type="prescriptionType"
      @confirm="confirmItemCid"
      @cancel="cancelItemCid"
    />

    <!-- Revisar (preview com letterhead) -->
    <PrescriptionReviewDialog
      v-model="showReviewDialog"
      :content="reviewContent"
      :prescription-type="prescriptionType"
      :selected-items-length="selectedItems.length"
      :letterhead-id="letterheadId"
      :content-area="letterheadContentArea"
    />

    <!-- Revisão dos itens ditados por voz -->
    <DictationReviewDialog
      v-model="showDictationReview"
      :items="parsedDictationItems"
      :prescription-type="prescriptionType"
      :patient-id="patientId"
      @confirm="confirmDictationItems"
      @cancel="cancelDictationReview"
    />
  </div>
</template>

<script>
import axios from "axios";
import http from "@/services/http";
import { mapGetters } from "vuex";
import MedicationDetailsDialog from "@/features/patients/prescriptions/dialogs/MedicationDetailsDialog.vue";
import PrescriptionReviewDialog from "@/features/patients/prescriptions/PrescriptionReviewDialog.vue";
import DictationButton from "@/features/patients/prescriptions/components/DictationButton.vue";
import DictationReviewDialog from "@/features/patients/prescriptions/components/DictationReviewDialog.vue";
import dictationStructuredMixin from "@/features/patients/prescriptions/mixins/dictationStructuredMixin.js";
import { printPrescription } from "@/features/patients/prescriptions/utils/printPrescription.js";
import {
  resolvePreferredLetterhead,
  fetchLetterheadImageUrl,
} from "@/services/letterheadService";
import ItemCidConfirmDialog from "@/features/patients/prescriptions/dialogs/ItemCidConfirmDialog.vue";
import { getMedicationsByPatient } from "@/services/patientMedicationService";
import { getSupplementsByPatient } from "@/services/supplementService";
import { getExamsByPatient } from "@/services/examService";
import tourDialogMixin from "@/features/tour/tourDialogMixin";

export default {
  name: "PrescriptionDialog",
  mixins: [dictationStructuredMixin, tourDialogMixin],
  components: {
    MedicationDetailsDialog,
    PrescriptionReviewDialog,
    DictationButton,
    DictationReviewDialog,
    ItemCidConfirmDialog,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.loadLetterhead();
        this.loadProntuario();
        this.tourOnOpen();
      } else {
        // Garante que o microfone seja desligado ao fechar o dialog
        // por qualquer caminho (X, Cancelar, exit-without-save, success).
        this.$refs.dictation?.stop();
      }
    },
    // Checagem de interacao medicamentosa em tempo real (T1.6c), com debounce.
    selectedItems: {
      handler() {
        this.scheduleInteractionCheck();
      },
      deep: true,
    },
    prescriptionType() {
      this.scheduleInteractionCheck();
    },
  },
  computed: {
    tourId() {
      return "dialog-prescricao";
    },
    showInteractionAlert() {
      return (
        this.prescriptionType === "medication" &&
        (this.interactionPairs.length > 0 ||
          this.interactionUnrecognized.length > 0)
      );
    },
    // Grave -> error; interacao nao-grave -> warning; so "nao verifiquei" ->
    // info, porque nao e alerta de risco, e aviso de cobertura da base.
    interactionAlertType() {
      if (this.interactionHasCritical) return "error";
      if (this.interactionPairs.length > 0) return "warning";
      return "info";
    },
    ...mapGetters({
      myUserId: "auth/userId",
      myUserType: "auth/userType",
    }),
    filteredItems() {
      if (this.prescriptionType === "medication") return this.medications;
      if (this.prescriptionType === "exam") return this.exams;
      if (this.prescriptionType === "supplement") return this.supplements;
      return [];
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    patientId() {
      return this.$route.params.id;
    },
  },
  data() {
    return {
      prescriptionType: "medication",
      previousPrescriptionType: "medication",
      characterCount: 0,
      isFocused: false,
      hasError: false,
      isSaving: false,
      apiLoading: false,
      showAutocomplete: false,
      searchQuery: "",
      medications: [],
      // O que o paciente JA tem registrado, por tipo. Carregado na abertura
      // para entrar na busca do "@" — ver `doProntuario`.
      prontuario: { medication: [], supplement: [], exam: [] },
      exams: [],
      supplements: [],
      selectedItems: [],
      selectedIndex: 0,
      // Janela de confirmação de CID para exame/suplemento (busca)
      showItemCidDialog: false,
      pendingDirectItem: null,
      menuPosition: { x: 0, y: 0 },
      savedRange: null,
      savedSelection: null,
      lastCursorRange: null,
      showMedicationDetails: false,
      currentMedication: null,
      editingIndex: null,
      // Review / letterhead
      showReviewDialog: false,
      reviewContent: "",
      letterheadId: null,
      letterheadContentArea: null,
      // Interacao medicamentosa (T1.6c)
      interactionPairs: [],
      interactionUnrecognized: [],
      interactionHasCritical: false,
      interactionChecking: false,
      interactionTimer: null,
    };
  },
  methods: {
    // Agenda a checagem de interacao com debounce (evita 1 request por tecla).
    scheduleInteractionCheck() {
      clearTimeout(this.interactionTimer);
      this.interactionTimer = setTimeout(() => this.checkInteractions(), 400);
    },
    // Consulta a base propria (T1.6c). Falha nunca bloqueia a prescricao.
    async checkInteractions() {
      if (this.prescriptionType !== "medication") {
        this.resetInteractionState();
        return;
      }
      const drugs = this.selectedItems
        .map((i) => i.name)
        .filter(Boolean);
      if (drugs.length < 2) {
        this.resetInteractionState();
        return;
      }
      try {
        this.interactionChecking = true;
        const { data } = await http.post("/api/prescription/check-interactions", {
          drugs,
          patient_id: this.patientId,
        });
        this.interactionPairs = data.pairs || [];
        this.interactionUnrecognized = data.unrecognized || [];
        this.interactionHasCritical = !!data.has_critical;
      } catch (e) {
        // Degradacao graciosa: a checagem falhou, entao nao ha o que afirmar.
        this.resetInteractionState();
      } finally {
        this.interactionChecking = false;
      }
    },
    resetInteractionState() {
      this.interactionPairs = [];
      this.interactionUnrecognized = [];
      this.interactionHasCritical = false;
    },
    tourSteps() {
      return [
        {
          title: "Nova Prescrição",
          description:
            "Aqui você monta prescrições de medicamentos, exames ou suplementos com busca inteligente, posologia e CIDs. Veja como funciona.",
        },
        {
          anchor: "pd2-tipo",
          title: "Escolha o tipo",
          description:
            "Selecione se vai prescrever medicamentos, exames ou suplementos. Cada tipo tem sua própria busca e formatação.",
        },
        {
          anchor: "pd2-busca",
          title: "Busque e adicione itens",
          description:
            "Digite a prescrição direto no editor. Use @ para buscar o item na base — ao escolher, você define posologia e o CID, e ele é inserido no texto.",
        },
        {
          anchor: "pd2-ditado",
          title: "Ditado por voz",
          description:
            "Prefere falar? Ative o ditado para transcrever a prescrição por voz e revisar os itens reconhecidos antes de inserir.",
        },
        {
          anchor: "pd2-itens",
          title: "Itens adicionados",
          description:
            "Este contador mostra quantos itens já foram incluídos na prescrição. Cada item aparece destacado no editor, com botão para remover.",
        },
        {
          anchor: "pd2-salvar",
          title: "Salvar prescrição",
          description:
            "Ao terminar, salve a prescrição. Você também pode revisar com o timbre ou imprimir antes de finalizar.",
        },
      ];
    },
    // Helpers de tipo (centralizam labels/cores/ícones de medication, exam e supplement)
    typeColor(type) {
      return (
        {
          medication: "primary",
          exam: "success",
          supplement: "warning",
        }[type] || "grey"
      );
    },
    typeIcon(type) {
      return (
        {
          medication: "mdi-pill",
          exam: "mdi-test-tube",
          supplement: "mdi-leaf",
        }[type] || "mdi-help"
      );
    },
    typeLabelSingular(type) {
      return (
        {
          medication: "medicamento",
          exam: "exame",
          supplement: "suplemento",
        }[type] || type
      );
    },
    typeLabelPlural(type) {
      return (
        {
          medication: "medicamentos",
          exam: "exames",
          supplement: "suplementos",
        }[type] || type
      );
    },
    typeLabelPluralCapitalized(type) {
      return (
        {
          medication: "Medicamentos",
          exam: "Exames",
          supplement: "Suplementos",
        }[type] || type
      );
    },
    lineClassForType(type) {
      return (
        {
          medication: "medication-line",
          exam: "exam-line",
          supplement: "supplement-line",
        }[type] || "exam-line"
      );
    },
    async handlePrescriptionTypeChange(newValue) {
      if (this.characterCount > 0 || this.selectedItems.length > 0) {
        const previousType = this.previousPrescriptionType;
        // Reverte pro tipo anterior enquanto o usuário decide
        this.$nextTick(() => {
          this.prescriptionType = previousType;
        });

        const ok = await this.$confirm({
          type: "warning",
          title: "Confirmar mudança",
          message:
            "Mudar o tipo de prescrição irá limpar todo o conteúdo. Deseja continuar?",
          confirmText: "Continuar",
        });
        if (!ok) return;

        this.prescriptionType = newValue;
        this.previousPrescriptionType = newValue;
        this.resetForm();
      } else {
        this.previousPrescriptionType = newValue;
        this.prescriptionType = newValue;
        this.resetForm();
      }
    },
    format(command) {
      document.execCommand(command, false, null);
      this.$refs.editor.focus();
    },
    clearFormatting() {
      document.execCommand("removeFormat", false, null);
      this.$refs.editor.focus();
    },
    updateContent() {
      const content = this.$refs.editor.innerText || "";
      this.characterCount = content.length;
      this.hasError = false;
    },
    handleKeydown(e) {
      if (this.showAutocomplete) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          this.selectedIndex = Math.min(
            this.selectedIndex + 1,
            this.filteredItems.length - 1,
          );
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        } else if (e.key === "Enter") {
          e.preventDefault();
          if (this.filteredItems[this.selectedIndex]) {
            this.handleItemSelection(this.filteredItems[this.selectedIndex]);
          }
        } else if (e.key === "Escape") {
          e.preventDefault();
          this.showAutocomplete = false;
        }
      }
      if (e.ctrlKey || e.metaKey) {
        if (e.key === "b") {
          e.preventDefault();
          this.format("bold");
        } else if (e.key === "i") {
          e.preventDefault();
          this.format("italic");
        } else if (e.key === "u") {
          e.preventDefault();
          this.format("underline");
        }
      }
    },
    handleBlur() {
      setTimeout(() => {
        this.isFocused = false;
      }, 200);
    },
    handlePaste(e) {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");
      document.execCommand("insertText", false, text);
    },
    async checkAutocomplete() {
      const selection = window.getSelection();
      if (selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      const textNode = range.startContainer;
      const text = textNode.textContent || "";
      const cursorPos = range.startOffset;
      let atIndex = -1;
      for (let i = cursorPos - 1; i >= 0; i--) {
        if (text[i] === "@") {
          atIndex = i;
          break;
        }
        if (text[i] === " " || text[i] === "\n") break;
      }
      if (atIndex !== -1) {
        this.searchQuery = text.substring(atIndex + 1, cursorPos).trim();
        const rect = range.getBoundingClientRect();
        this.menuPosition = {
          x: rect.left + window.scrollX,
          y: rect.top + window.scrollY + 20,
        };
        this.showAutocomplete = true;
        this.selectedIndex = 0;
        if (this.searchQuery.length > 0) {
          if (this.prescriptionType === "medication") {
            await this.fetchMedicationsFromAPI(this.searchQuery);
          } else if (this.prescriptionType === "exam") {
            await this.fetchExamsFromAPI(this.searchQuery);
          } else if (this.prescriptionType === "supplement") {
            await this.fetchSupplementsFromAPI(this.searchQuery);
          }
        } else {
          this.medications = [];
          this.exams = [];
          this.supplements = [];
        }
      } else {
        this.showAutocomplete = false;
      }
    },
    // Carrega o que ja esta no prontuario do paciente. Falha aqui nao pode
    // impedir de prescrever: a busca no catalogo externo segue funcionando.
    // Carrega o prontuario do paciente nos tres tipos de uma vez. Falha aqui
    // nao pode impedir de prescrever: cada lista cai para vazia sozinha e a
    // busca no catalogo externo segue funcionando.
    async loadProntuario() {
      if (!this.patientId) {
        this.prontuario = { medication: [], supplement: [], exam: [] };
        return;
      }

      const busca = (promessa, oQue) =>
        promessa.catch((err) => {
          console.error(`Erro ao carregar ${oQue} do paciente:`, err);
          return [];
        });

      const [medication, supplement, exam] = await Promise.all([
        busca(getMedicationsByPatient(this.patientId), "medicamentos"),
        busca(getSupplementsByPatient(this.patientId), "suplementos"),
        busca(getExamsByPatient(this.patientId), "exames"),
      ]);

      this.prontuario = { medication, supplement, exam };
    },
    // Mesma chave que o backend usa para deduplicar (nome + dosagem, sem caixa).
    itemKey(item) {
      const nome = (item.name || "").trim().toLowerCase();
      const dose = (item.dosage || "").trim().toLowerCase();
      return `${nome}|${dose}`;
    },
    // Normaliza uma linha do prontuario para o formato da lista do "@".
    // Cada tipo guarda o nome e o estado em colunas diferentes.
    normalizarDoProntuario(tipo, linha) {
      if (tipo === "medication") {
        const emUso = !linha.status || linha.status === "using";
        return {
          name: linha.name,
          dosage: linha.dosage || "",
          description:
            [linha.dosage, linha.frequency].filter(Boolean).join(" | ") ||
            "Sem posologia registrada",
          tuss_code: linha.tuss_code || null,
          patientBadge:
            linha.status === "stopped" ? "Parou de usar" : emUso ? "Em uso" : "Não sei",
          patientActive: emUso,
        };
      }

      if (tipo === "supplement") {
        return {
          name: linha.name,
          dosage: linha.dosage || "",
          description:
            [linha.dosage, linha.frequency].filter(Boolean).join(" | ") ||
            "Sem posologia registrada",
          tuss_code: linha.tuss_code || null,
          patientBadge: linha.active === false ? "Inativo" : "Em uso",
          patientActive: linha.active !== false,
        };
      }

      // exame
      const pendente = linha.status === "pending";
      return {
        name: linha.exam_name,
        dosage: "",
        description: linha.date
          ? `Solicitado em ${new Date(linha.date).toLocaleDateString("pt-BR")}`
          : "Sem data registrada",
        tuss_code: linha.tuss_code || null,
        patientBadge: pendente ? "Já solicitado" : "Já realizado",
        patientActive: pendente,
      };
    },
    // O que o paciente ja tem, filtrado pelo que ele digitou depois do "@".
    doProntuario(tipo, query) {
      const alvo = (query || "").trim().toLowerCase();
      if (!alvo) return [];

      return (this.prontuario[tipo] || [])
        .filter((linha) => {
          const nome = linha.name || linha.exam_name || "";
          return nome.toLowerCase().includes(alvo);
        })
        .slice(0, 5)
        .map((linha) => ({
          ...this.normalizarDoProntuario(tipo, linha),
          presentation: "",
          // Marca de origem: o item vem do prontuario, nao do catalogo.
          fromPatient: true,
        }));
    },
    /**
     * Junta o prontuario ao resultado do catalogo externo.
     *
     * O que o paciente JA tem vem primeiro e SEMPRE aparece. Antes, o "@"
     * consultava so o catalogo: um item lancado pelo card era invisivel aqui, o
     * medico digitava o nome de novo, pegava a entrada do catalogo e acabava com
     * duas linhas da mesma coisa no prontuario — foi assim que nasceu o
     * pantoprazol duplicado.
     *
     * Mesmo nome na mesma dose aparece uma vez so, com o do prontuario vencendo:
     * e ele que carrega a posologia que o paciente usa.
     */
    mesclarComProntuario(doProntuario, doCatalogo) {
      const jaListados = new Set(doProntuario.map(this.itemKey));

      return [
        ...doProntuario,
        ...doCatalogo.filter((i) => !jaListados.has(this.itemKey(i))),
      ];
    },
    async fetchMedicationsFromAPI(query) {
      this.apiLoading = true;
      const doProntuario = this.doProntuario("medication", query);

      try {
        const res = await http.post(
          "/api/prescription/search-medication",
          { texto: query },
          { headers: { "Content-Type": "application/json" } },
        );

        const doCatalogo = (res.data?.candidatos || []).slice(0, 10).map((item) => ({
          name: item.nome,
          dosage: item.dosagem || "",
          presentation: item.apresentacao || "",
          description: `${item.dosagem} | ${item.apresentacao}`,
          tuss_code: item.id_tuss,
          score: item.score,
        }));

        this.medications = this.mesclarComProntuario(doProntuario, doCatalogo);
      } catch (err) {
        console.error("Erro ao buscar medicamentos:", err);
        // Catalogo fora do ar nao pode esconder o proprio prontuario.
        this.medications = doProntuario;
      } finally {
        this.apiLoading = false;
      }
    },
    async fetchExamsFromAPI(query) {
      this.apiLoading = true;
      const doProntuario = this.doProntuario("exam", query);

      try {
        const res = await http.post(
          "/api/prescription/search-exams",
          { texto: query },
          { headers: { "Content-Type": "application/json" } },
        );

        const doCatalogo = (res.data?.candidatos || []).slice(0, 10).map((item) => ({
          name: item.descricao_encontrada,
          tuss_code: item.codigo_tuss,
          similarity: item.similaridade,
          description: `Código TUSS: ${item.codigo_tuss} | Similaridade: ${item.similaridade}%`,
        }));

        this.exams = this.mesclarComProntuario(doProntuario, doCatalogo);
      } catch (err) {
        console.error("Erro ao buscar exames:", err);
        this.exams = doProntuario;
      } finally {
        this.apiLoading = false;
      }
    },
    async fetchSupplementsFromAPI(query) {
      this.apiLoading = true;
      const doProntuario = this.doProntuario("supplement", query);

      try {
        const res = await http.post(
          "/api/prescription/search-supplement",
          { texto: query },
          { headers: { "Content-Type": "application/json" } },
        );

        const doCatalogo = (res.data?.candidatos || []).slice(0, 10).map((item) => ({
          name: item.nome,
          category: item.categoria || "",
          description: item.categoria || "",
          tuss_code: item.id_tuss,
          score: item.score,
        }));

        this.supplements = this.mesclarComProntuario(doProntuario, doCatalogo);
      } catch (err) {
        console.error("Erro ao buscar suplementos:", err);
        this.supplements = doProntuario;
      } finally {
        this.apiLoading = false;
      }
    },
    handleItemSelection(item) {
      if (this.prescriptionType === "medication") {
        this.openMedicationDetailsDialog(item);
      } else if (this.prescriptionType === "exam" || this.prescriptionType === "supplement") {
        // Exame/suplemento: abre a janela de CID antes de inserir.
        this.openItemCidDialog(item);
      }
    },
    // Captura a posição do "@" e abre a janela de CID (exame/suplemento).
    openItemCidDialog(item) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        this.savedRange = range.cloneRange();
        const textNode = range.startContainer;
        const text = textNode.textContent || "";
        const cursorPos = range.startOffset;
        let atIndex = -1;
        for (let i = cursorPos - 1; i >= 0; i--) {
          if (text[i] === "@") {
            atIndex = i;
            break;
          }
        }
        this.savedSelection = { textNode, text, cursorPos, atIndex };
      }
      this.pendingDirectItem = item;
      this.showAutocomplete = false;
      this.showItemCidDialog = true;
    },
    confirmItemCid(cidIds) {
      const item = { ...this.pendingDirectItem, condition_ids: cidIds };
      this.showItemCidDialog = false;
      this.pendingDirectItem = null;
      this.insertItemDirectly(item);
    },
    cancelItemCid() {
      this.showItemCidDialog = false;
      this.pendingDirectItem = null;
      this.savedRange = null;
      this.savedSelection = null;
    },
    // A seleção do "@" já foi capturada em openItemCidDialog.
    insertItemDirectly(exam) {
      const itemWithDetails = {
        ...exam,
        type: this.prescriptionType,
        position: this.selectedItems.length,
        timestamp: new Date().toISOString(),
      };
      this.selectedItems.push(itemWithDetails);
      this.insertItemInEditor(itemWithDetails);
      this.showAutocomplete = false;
      this.$nextTick(() => this.updateContent());
      setTimeout(() => {
        this.$refs.editor.focus();
        if (this.lastCursorRange) {
          try {
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(this.lastCursorRange);
          } catch (e) {
            console.warn("Não foi possível restaurar cursor:", e);
          }
        }
        this.savedRange = null;
        this.savedSelection = null;
        this.lastCursorRange = null;
      }, 100);
    },
    openMedicationDetailsDialog(medication) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        this.savedRange = range.cloneRange();

        const textNode = range.startContainer;
        const text = textNode.textContent || "";
        const cursorPos = range.startOffset;

        let atIndex = -1;
        for (let i = cursorPos - 1; i >= 0; i--) {
          if (text[i] === "@") {
            atIndex = i;
            break;
          }
        }
        this.savedSelection = { textNode, text, cursorPos, atIndex };
      }
      this.currentMedication = medication;
      this.showAutocomplete = false;
      this.showMedicationDetails = true;
      this.editingIndex = null;
    },
    confirmMedicationDetails(details) {
      const medicationWithDetails = {
        ...this.currentMedication,
        ...details,
        type: "medication",
        position: this.selectedItems.length,
        timestamp: new Date().toISOString(),
      };
      this.selectedItems.push(medicationWithDetails);
      this.insertItemInEditor(medicationWithDetails);
      this.showMedicationDetails = false;
      this.$nextTick(() => this.updateContent());
      setTimeout(() => {
        this.$refs.editor?.focus();
        // Restaura o cursor logo após o item recém-inserido
        // (o focus() acima move o cursor, por isso precisamos reaplicar o range)
        if (this.lastCursorRange) {
          try {
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(this.lastCursorRange);
          } catch (e) {
            console.warn("Não foi possível restaurar cursor:", e);
          }
        }
        this.savedRange = null;
        this.savedSelection = null;
        this.lastCursorRange = null;
      }, 100);
    },

    insertItemInEditor(item) {
      const editor = this.$refs.editor;
      if (!editor) return;
      let itemText = "";
      if (item.type === "medication") {
        itemText = `${item.name}`;
        if (item.dosage) itemText += ` - ${item.dosage}`;
        if (item.quantidade) itemText += ` - ${item.quantidade}`;
        if (item.frequencia) itemText += ` - ${item.frequencia}`;
        if (item.duracao) itemText += ` - ${item.duracao}`;
        if (item.observacoes) itemText += ` (${item.observacoes})`;
      } else {
        itemText = `${item.name}`;
      }
      const selection = window.getSelection();
      if (this.savedRange && this.savedSelection) {
        try {
          selection.removeAllRanges();
          selection.addRange(this.savedRange);
          const { textNode, text, cursorPos, atIndex } = this.savedSelection;
          if (atIndex !== -1 && textNode.nodeType === Node.TEXT_NODE) {
            const wrapper = document.createElement("div");
            wrapper.className = this.lineClassForType(item.type);
            wrapper.setAttribute("data-tuss", item.tuss_code || "");
            wrapper.setAttribute("data-item-index", item.position);
            wrapper.setAttribute("data-item-type", item.type);
            wrapper.style.display = "flex";
            wrapper.style.alignItems = "center";
            wrapper.style.justifyContent = "space-between";
            wrapper.style.gap = "8px";
            const textSpan = document.createElement("span");
            textSpan.style.flex = "1";
            textSpan.textContent = itemText;
            const deleteBtn = document.createElement("button");
            deleteBtn.innerHTML = "×";
            deleteBtn.type = "button";
            deleteBtn.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.removeItem(item.position);
            };
            wrapper.appendChild(textSpan);
            wrapper.appendChild(deleteBtn);
            const before = text.substring(0, atIndex);
            const after = text.substring(cursorPos);
            textNode.textContent = before;
            const parent = textNode.parentNode;
            if (before.trim() !== "") {
              const br1 = document.createElement("br");
              parent.insertBefore(br1, textNode.nextSibling);
              parent.insertBefore(wrapper, br1.nextSibling);
            } else {
              parent.insertBefore(wrapper, textNode.nextSibling);
            }
            const br2 = document.createElement("br");
            parent.insertBefore(br2, wrapper.nextSibling);
            if (after) {
              const afterText = document.createTextNode(after);
              parent.insertBefore(afterText, br2.nextSibling);
              const newRange = document.createRange();
              newRange.setStart(afterText, 0);
              newRange.collapse(true);
              selection.removeAllRanges();
              selection.addRange(newRange);
              this.lastCursorRange = newRange.cloneRange();
            } else {
              const spaceText = document.createTextNode(" ");
              parent.insertBefore(spaceText, br2.nextSibling);
              const newRange = document.createRange();
              newRange.setStart(spaceText, 1);
              newRange.collapse(true);
              selection.removeAllRanges();
              selection.addRange(newRange);
              this.lastCursorRange = newRange.cloneRange();
            }
          }
        } catch (error) {
          console.error("Erro ao inserir item:", error);
          editor.appendChild(document.createElement("br"));
          const wrapper = document.createElement("div");
          wrapper.className = this.lineClassForType(item.type);
          wrapper.style.display = "flex";
          wrapper.style.alignItems = "center";
          wrapper.style.justifyContent = "space-between";
          const textSpan = document.createElement("span");
          textSpan.style.flex = "1";
          textSpan.textContent = itemText;
          wrapper.appendChild(textSpan);
          editor.appendChild(wrapper);
          editor.appendChild(document.createElement("br"));
        }
      }
    },
    removeItem(position) {
      this.selectedItems = this.selectedItems.filter(
        (item) => item.position !== position,
      );
      const itemWrapper = this.$refs.editor.querySelector(
        `[data-item-index="${position}"]`,
      );
      if (itemWrapper && itemWrapper.parentNode) {
        const parent = itemWrapper.parentNode;
        const nextSibling = itemWrapper.nextSibling;
        const prevSibling = itemWrapper.previousSibling;
        if (nextSibling && nextSibling.nodeName === "BR")
          parent.removeChild(nextSibling);
        if (prevSibling && prevSibling.nodeName === "BR")
          parent.removeChild(prevSibling);
        parent.removeChild(itemWrapper);
      }
      this.$nextTick(() => this.updateContent());
    },
    cancelMedicationDetails() {
      this.showMedicationDetails = false;
      this.currentMedication = null;
      this.editingIndex = null;
      this.savedRange = null;
      this.savedSelection = null;
      setTimeout(() => this.$refs.editor?.focus(), 100);
    },
    async clearAll() {
      const ok = await this.$confirm({
        type: "delete",
        title: "Confirmar limpeza",
        message: "Tem certeza que deseja limpar toda a prescrição?",
        hint: "Esta ação não pode ser desfeita.",
        confirmText: "Limpar Tudo",
      });
      if (!ok) return;
      this.resetForm();
      this.$refs.editor?.focus();
    },
    async printPrescription() {
      const content = this.$refs.editor?.innerHTML || "";
      if (!content || content.trim() === "") {
        this.$confirm({ type: "warning", title: "Atenção", message: "Não há conteúdo." });
        return;
      }

      // Busca a imagem do timbre (se houver) e converte pra dataURL, que
      // sobrevive ao window.open da impressão.
      let letterheadImageUrl = null;
      if (this.letterheadId) {
        let blobUrl = null;
        try {
          blobUrl = await fetchLetterheadImageUrl(this.letterheadId);
          letterheadImageUrl = await this.blobUrlToDataUrl(blobUrl);
        } catch (e) {
          console.warn("Não foi possível carregar o timbre para impressão:", e);
        } finally {
          if (blobUrl) URL.revokeObjectURL(blobUrl);
        }
      }

      try {
        printPrescription({
          content,
          prescriptionType: this.prescriptionType,
          selectedItemsLength: this.selectedItems.length,
          letterheadImageUrl,
          contentArea: this.letterheadContentArea,
        });
      } catch (error) {
        this.$confirm({ type: "warning", title: "Atenção", message: "Não há conteúdo." });
      }
    },

    async blobUrlToDataUrl(blobUrl) {
      const res = await fetch(blobUrl);
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },

    openReview() {
      const content = this.$refs.editor?.innerHTML || "";
      if (!content || content.trim() === "") {
        this.$confirm({ type: "warning", title: "Atenção", message: "Não há conteúdo." });
        return;
      }
      this.reviewContent = content;
      this.showReviewDialog = true;
    },

    onDictationToggle(isRecording) {
      // Quando inicia a gravação, foca o editor pra que execCommand('insertText')
      // tenha um caret válido pra inserir o texto.
      if (isRecording) {
        const editor = this.$refs.editor;
        if (!editor) return;
        editor.focus();
        // Se não há seleção (cursor) dentro do editor, posiciona no final.
        const selection = window.getSelection();
        const inEditor =
          selection.rangeCount > 0 &&
          editor.contains(selection.getRangeAt(0).startContainer);
        if (!inEditor) {
          const range = document.createRange();
          range.selectNodeContents(editor);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    },

    insertDictatedText(text) {
      if (!text) return;
      const editor = this.$refs.editor;
      if (!editor) return;
      editor.focus();
      // Adiciona um espaço no final pra continuar o ditado fluido
      document.execCommand("insertText", false, text.trim() + " ");
      this.updateContent();
    },

    async loadLetterhead() {
      try {
        // Respeita o timbre escolhido no gerenciador (perfil ou clínica) — o
        // mesmo que o download usa, para impressão e PDF não divergirem.
        const lh = await resolvePreferredLetterhead();
        this.letterheadId = lh?.id || null;
        this.letterheadContentArea = lh?.content_area || null;
      } catch (err) {
        // Silencioso — sem timbre cai pra formatação padrão
        this.letterheadId = null;
        this.letterheadContentArea = null;
      }
    },
    async closeDialog() {
      if (this.characterCount > 0) {
        const ok = await this.$confirm({
          type: "warning",
          title: "Sair sem salvar?",
          message:
            "Você tem conteúdo não salvo. Deseja realmente sair sem salvar?",
          confirmText: "Sair sem Salvar",
        });
        if (!ok) return;
      }
      this.$emit("input", false);
      this.resetForm();
    },
    resetForm() {
      this.$refs.editor.innerHTML = "";
      this.selectedItems = [];
      this.medications = [];
      this.exams = [];
      this.supplements = [];
      this.characterCount = 0;
      this.hasError = false;
      this.savedRange = null;
      this.savedSelection = null;
      this.dictationBuffer = "";
      this.parsedDictationItems = [];
      this.showDictationReview = false;
    },
    async savePrescription() {
      const content = this.$refs.editor.innerHTML;
      if (!content || content.trim() === "") {
        this.hasError = true;
        return;
      }
      if (this.selectedItems.length === 0) {
        this.$confirm({
          type: "warning",
          title: "Atenção",
          message: `Por favor, selecione pelo menos um ${this.typeLabelSingular(
            this.prescriptionType,
          )} usando @`,
        });
        return;
      }
      this.isSaving = true;
      try {
        const patientId = this.$route.params.id;
        const doctorId =
          this.myUserId || this.$store?.state?.auth?.user?.id || null;
        if (!patientId) {
          this.$confirm({
            type: "error",
            title: "Erro",
            message: "ID do paciente não encontrado",
          });
          return;
        }
        if (!doctorId) {
          this.$confirm({
            type: "error",
            title: "Erro",
            message: "ID do médico não encontrado (Vuex)",
          });
          return;
        }
        const prescriptionData = {
          patient_id: patientId,
          doctor_id: doctorId,
          prescription_type: this.prescriptionType,
          full_content: content,
          status: "active",
          items: this.selectedItems.map((item) => ({
            item_type: this.prescriptionType,
            tuss_code: item.tuss_code || null,
            medication_name: item.name,
            dosage: item.dosage || null,
            presentation: item.presentation || null,
            quantity: item.quantidade || null,
            frequency: item.frequencia || null,
            duration: item.duracao || null,
            administration_route: item.via_administracao || null,
            notes: item.observacoes || null,
            score: item.score || null,
            condition_ids: item.condition_ids || [],
          })),
        };
        const response = await http.post(
          `/api/prescription/create`,
          prescriptionData,
        );
        this.$emit("saved", response.data);
        const itemType = this.typeLabelPlural(this.prescriptionType);
        this.isSaving = false;
        await this.$confirm({
          type: "success",
          title: "Sucesso!",
          message: `Prescrição salva com sucesso!\n\n${this.selectedItems.length} ${itemType}`,
        });
        this.$emit("input", false);
        this.resetForm();
        return;
      } catch (error) {
        console.error("❌ Erro ao salvar:", error);
        let errorMessage = "Erro ao salvar. Tente novamente.";
        if (error.response?.data?.message)
          errorMessage = error.response.data.message;
        else if (error.response?.data?.errors) {
          errorMessage = Object.values(error.response.data.errors)
            .flat()
            .join("\n");
        } else if (error.message) errorMessage = error.message;
        this.$confirm({ type: "error", title: "Erro", message: errorMessage });
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
/* ═══ Design system dialog ═══ */
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.close-btn {
  position: relative;
  z-index: 1;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

/* ═══ Toolbar de formatação ═══ */
.ds-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #F4F9F6;
  border: 1px solid var(--ds-blue-100);
  border-radius: 8px;
}

.ds-toolbar :deep(.v-btn) {
  text-transform: none;
}

.footer-chip {
  font-size: 11px !important;
  font-weight: 600 !important;
}

.ds-cancel-btn,
.ds-save-btn {
  font-size: 13px !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  text-transform: none !important;
  height: 40px !important;
  border-radius: 8px !important;
}

.ds-save-btn {
  box-shadow: 0 4px 12px rgba(31, 77, 63, 0.22) !important;
}

/* ═══ Editor ═══ */
.editor-wrapper {
  border: 2px solid var(--ds-blue-100);
  border-radius: 8px;
  background: var(--ds-surface-white);
  transition: all 0.25s ease;
  position: relative;
}

.editor-wrapper.editor-focused {
  border-color: var(--ds-primary);
  box-shadow: 0 0 0 3px rgba(31, 77, 63, 0.12);
}

.editor-wrapper.editor-error {
  border-color: rgb(var(--v-theme-error));
}

.rich-editor {
  min-height: 160px;
  max-height: 34vh;
  width: 100%;
  padding: 16px;
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ds-text);
}

.rich-editor:focus {
  outline: none;
}

.rich-editor:empty:before {
  content: attr(placeholder);
  color: var(--ds-gray-light);
  pointer-events: none;
  font-style: italic;
}

.editor-loading {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* ⭐ ESTILO DA LINHA DO MEDICAMENTO */
.rich-editor :deep(.medication-line) {
  display: block;
  background-color: var(--ds-blue-100);
  padding: 8px 12px;
  border-radius: 4px;
  margin-top: 4px;
  margin-bottom: 4px;
  border-left: 4px solid var(--ds-primary);
  font-weight: 500;
  line-height: 1.5;
  transition: background-color 0.2s ease;
}

.rich-editor :deep(.medication-line:hover) {
  background-color: var(--ds-blue-200);
}

/* ⭐ ESTILO DA LINHA DO EXAME */
.rich-editor :deep(.exam-line) {
  display: block;
  background-color: var(--ds-green-surface);
  padding: 8px 12px;
  border-radius: 4px;
  margin-top: 4px;
  margin-bottom: 4px;
  border-left: 4px solid var(--ds-green);
  font-weight: 500;
  line-height: 1.5;
  transition: background-color 0.2s ease;
}

.rich-editor :deep(.exam-line:hover) {
  background-color: var(--ds-green-200);
}

/* ⭐ ESTILO DA LINHA DO SUPLEMENTO */
.rich-editor :deep(.supplement-line) {
  display: block;
  background-color: var(--ds-primary-surface);
  padding: 8px 12px;
  border-radius: 4px;
  margin-top: 4px;
  margin-bottom: 4px;
  border-left: 4px solid #fb8c00;
  font-weight: 500;
  line-height: 1.5;
  transition: background-color 0.2s ease;
}

.rich-editor :deep(.supplement-line:hover) {
  background-color: var(--ds-primary-border);
}

.selected-item {
  background-color: rgba(var(--v-theme-primary), 0.15) !important;
}

.rich-editor :deep(strong) {
  font-weight: 700;
}

.rich-editor :deep(em) {
  font-style: italic;
}

.rich-editor :deep(u) {
  text-decoration: underline;
}

.rich-editor :deep(ul),
.rich-editor :deep(ol) {
  padding-left: 24px;
  margin: 8px 0;
}

.rich-editor :deep(li) {
  margin: 4px 0;
}

.medication-line,
.exam-line,
.supplement-line {
  position: relative;
}

/* Separa "o que eu achei" de "o que eu nao consegui verificar" no alerta. */
.interaction-divider {
  border-top: 1px solid currentColor;
  opacity: 0.85;
}
</style>
